import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import Seo from "@/components/Seo";
import JourneyCard from "@/components/JourneyCard";
import { api } from "@/lib/api";
import { mediaUrl } from "@/lib/media";

const SORTS = [
  { value: "price-asc", label: "Price, low to high" },
  { value: "price-desc", label: "Price, high to low" },
  { value: "duration-asc", label: "Shortest to longest" },
  { value: "duration-desc", label: "Longest to shortest" },
];

const DURATION_FILTERS = [
  { value: "all", label: "All durations" },
  { value: "7", label: "7 days" },
  { value: "10", label: "10 days" },
  { value: "12", label: "12 days" },
  { value: "14", label: "14 days" },
  { value: "15plus", label: "15+ days" },
];

function bucketMatches(days, bucket) {
  if (bucket === "all") return true;
  const d = Number(days) || 0;
  if (bucket === "15plus") return d >= 15;
  const target = parseInt(bucket, 10);
  // a journey is in the "10 days" bucket if its duration is 10–11 days, etc.
  // 15+ catches anything above 14.
  const next = { 7: 10, 10: 12, 12: 14, 14: 15 }[target] ?? target + 1;
  return d >= target && d < next;
}

function sortJourneys(list, sortKey) {
  const arr = [...list];
  const priceOf = (j) => {
    const n = Number(j.from_price_gbp || 0);
    return n > 0 ? n : Number.POSITIVE_INFINITY; // no-price journeys sink to the end
  };
  const durOf = (j) => Number(j.duration_days || 0) || Number.POSITIVE_INFINITY;
  switch (sortKey) {
    case "price-desc":
      arr.sort((a, b) => {
        const pa = Number(a.from_price_gbp || 0); const pb = Number(b.from_price_gbp || 0);
        // priced first, descending; unpriced last
        if (!pa && !pb) return 0;
        if (!pa) return 1;
        if (!pb) return -1;
        return pb - pa;
      });
      break;
    case "duration-asc":
      arr.sort((a, b) => durOf(a) - durOf(b));
      break;
    case "duration-desc":
      arr.sort((a, b) => (Number(b.duration_days || 0)) - (Number(a.duration_days || 0)));
      break;
    case "price-asc":
    default:
      arr.sort((a, b) => priceOf(a) - priceOf(b));
  }
  return arr;
}

export default function DestinationDetail() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [journeys, setJourneys] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [sort, setSort] = useState("price-asc");
  const [bucket, setBucket] = useState("all");

  useEffect(() => {
    api.get(`/destinations/${slug}`).then((r) => setD(r.data)).catch(() => setD(false));
    api.get("/journeys").then((r) => setJourneys(r.data || []));
    api.get("/destinations").then((r) => setDestinations(r.data || []));
  }, [slug]);

  const destinationMap = useMemo(() => {
    const m = {};
    destinations.forEach((x) => { m[x.slug] = { slug: x.slug, name: x.name }; });
    return m;
  }, [destinations]);

  const linkedJourneys = useMemo(
    () => journeys.filter((j) => (j.destination_slugs || []).includes(slug)),
    [journeys, slug]
  );

  const visibleJourneys = useMemo(() => {
    const filtered = linkedJourneys.filter((j) => bucketMatches(j.duration_days, bucket));
    return sortJourneys(filtered, sort);
  }, [linkedJourneys, bucket, sort]);

  if (d === false) return <PublicLayout><div className="pt-40 px-6 max-w-[1500px] mx-auto"><p className="eyebrow">Not found</p></div></PublicLayout>;
  if (!d) return <PublicLayout><div className="pt-40 px-6 max-w-[1500px] mx-auto"><p className="eyebrow">Loading…</p></div></PublicLayout>;

  const seo = d.seo || {};
  return (
    <PublicLayout>
      <Seo title={seo.title} description={seo.description} og_title={seo.og_title} og_description={seo.og_description} />
      <section className="relative h-[80vh] min-h-[600px] w-full overflow-hidden">
        <img src={mediaUrl(d.hero_image)} alt={d.name} className="ken-img absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-jade/40" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-jade/70" />
        <div className="relative z-10 h-full flex items-end pb-20">
          <div className="max-w-[1500px] mx-auto px-6 md:px-12 w-full">
            <p className="eyebrow text-copper">{d.hero_subtitle || d.tagline}</p>
            <h1 className="mt-5 font-serif text-ivory font-light text-6xl md:text-8xl leading-[0.95]">{d.hero_title || d.name}</h1>
          </div>
        </div>
      </section>

      <section className="bg-ivory py-24 md:py-32">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <aside className="md:col-span-4 md:sticky md:top-32 self-start">
            <p className="eyebrow">At a glance</p>
            <ul className="mt-6 space-y-5 border-t border-mist/60 pt-6">
              {d.regions?.map((r) => (
                <li key={r.name}>
                  <p className="font-serif text-xl text-jade">{r.name}</p>
                  <p className="text-ink/60 text-[14px] mt-1">{r.note}</p>
                </li>
              ))}
            </ul>
            <Link to="/contact" data-testid="dest-cta" className="btn-primary mt-10 inline-flex">Begin a Journey</Link>
          </aside>
          <div className="md:col-span-8 space-y-10">
            <p className="text-ink/80 text-[17px] md:text-[19px] font-light leading-[1.8]">{d.description}</p>
            <div>
              <p className="eyebrow">Curated Experiences</p>
              <ul className="mt-6 divide-y divide-mist/60 border-y border-mist/60">
                {d.experiences?.map((e, i) => (
                  <li key={i} className="py-5 flex items-baseline gap-6">
                    <span className="font-serif text-jade text-2xl">·</span>
                    <span className="text-ink/85 text-[15px] md:text-[16px] font-light leading-relaxed">{e}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-sand py-24 md:py-32" data-testid="dest-related-journeys" id="sample-journeys">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          <div className="md:flex md:items-end md:justify-between gap-10">
            <div>
              <p className="eyebrow">Sample Private Journeys</p>
              <h2 className="mt-5 font-serif text-4xl md:text-5xl text-jade font-light max-w-2xl">
                {d.name}, privately journeyed.
              </h2>
              <p className="mt-4 text-ink/70 text-[15px] md:text-[16px] font-light max-w-xl">
                A starting point. Every itinerary we craft is shaped privately around you.
              </p>
            </div>

            {linkedJourneys.length > 0 && (
              <div className="mt-8 md:mt-0 flex items-center gap-4 shrink-0">
                <label className="eyebrow text-ink/55" htmlFor="journey-sort">Sort</label>
                <select
                  id="journey-sort"
                  data-testid="journey-sort"
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="border border-mist bg-ivory px-3 py-2 text-[13px] text-ink/85"
                >
                  {SORTS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {linkedJourneys.length > 0 && (
            <div className="mt-10 flex flex-wrap gap-2" data-testid="journey-filters">
              {DURATION_FILTERS.map((f) => {
                const active = bucket === f.value;
                return (
                  <button
                    key={f.value}
                    type="button"
                    data-testid={`journey-filter-${f.value}`}
                    onClick={() => setBucket(f.value)}
                    className={`text-[11px] tracking-[0.18em] uppercase px-4 py-2 border transition-colors ${
                      active
                        ? "border-jade bg-jade text-ivory"
                        : "border-mist text-ink/70 hover:border-jade hover:text-jade"
                    }`}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
          )}

          {visibleJourneys.length > 0 ? (
            <div className="mt-14 grid md:grid-cols-2 gap-10 md:gap-12" data-testid="journey-grid">
              {visibleJourneys.map((j) => (
                <JourneyCard key={j.slug} journey={j} destinationMap={destinationMap} />
              ))}
            </div>
          ) : (
            <div
              data-testid="journey-empty"
              className="mt-14 border border-mist/70 bg-ivory/60 px-8 py-14 md:py-20 text-center"
            >
              <p className="eyebrow text-copper">No journeys match</p>
              <p className="mt-5 font-serif text-2xl md:text-3xl text-jade font-light max-w-2xl mx-auto">
                {linkedJourneys.length === 0
                  ? "We are currently preparing sample journeys for this destination. Speak to us and we will design one around you."
                  : "Nothing fits these filters yet. Try a different duration, or speak to us about a bespoke itinerary."}
              </p>
              <Link
                to="/contact"
                data-testid="journey-empty-cta"
                className="btn-primary mt-10 inline-flex"
              >
                Begin a Journey
              </Link>
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
